// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (Quest review E1, E2, B7, D3, 2026-11-03): how a placed globe keeps
 * its height above the surface it was placed on, and what of a placement
 * is saved for the next session.
 *
 * A placement remembers the surface point (anchored where the device can)
 * and one of two holds:
 *   - **rest:** the globe rests on the surface: its bottom stays a small
 *     gap above it at any size, so growing it never sinks it into the
 *     table. A phone's Place and the marker scan both rest.
 *   - **height:** a headset's height step chose where the centre is. It
 *     stays there, except that a globe grown past it rises rather than
 *     pass through the surface.
 *
 * Pure numbers, no THREE, so it is unit-tested on its own
 * (`vrGlobeHold.test.ts`).
 */

/** Clear air between the shared marker and the underside of the sphere above it, metres. */
export const MARKER_GAP_M = 0.12
/** Clear air under a globe a phone rests on a table or the floor, metres. */
export const SURFACE_GAP_M = 0.02
/** The least clear air under a globe placed at a chosen height, metres. */
export const MIN_CLEARANCE_M = 0.02
/**
 * The size a marker scan gives the sphere (owner D3, 2026-11-03: 0.4–0.5
 * for phones round a table, 0.45 to start). Every phone that scans gets
 * the same size, so a room agrees on it.
 */
export const MARKER_GLOBE_SCALE = 0.45

export type GlobeHold =
  | { readonly kind: 'rest'; readonly gap: number }
  | { readonly kind: 'height'; readonly offset: number }

/** Height of the globe's centre above its surface point, for a globe of this radius (metres). */
export function holdOffset(hold: GlobeHold, radius: number): number {
  return hold.kind === 'rest' ? radius + hold.gap : Math.max(hold.offset, radius + MIN_CLEARANCE_M)
}

/**
 * The least height a headset's height step may put the centre at above
 * the surface point it chose: the globe just clear of the surface.
 */
export function lowestCentreAbove(surfaceY: number, radius: number): number {
  return surfaceY + radius + MIN_CLEARANCE_M
}

/** Further than this on the level, a globe restored from the last session is out of reach. */
export const OUT_OF_REACH_M = 4
/** Further round than this from where the viewer faces, a restored globe is behind them. */
const BEHIND_COS = Math.cos((100 * Math.PI) / 180)

interface Vec3Like {
  readonly x: number
  readonly y: number
  readonly z: number
}

/**
 * Whether a globe restored from the last session's anchor is somewhere
 * the viewer would not find it: behind them, or across the room. Measured
 * on the level, from the viewer's position and facing direction.
 */
export function isOutOfReach(viewer: Vec3Like, forward: Vec3Like, globe: Vec3Like): boolean {
  const dx = globe.x - viewer.x
  const dz = globe.z - viewer.z
  const distance = Math.hypot(dx, dz)
  if (distance > OUT_OF_REACH_M) return true
  // Right on top of the viewer: in reach whatever the angle.
  if (distance < 0.3) return false
  const facing = Math.hypot(forward.x, forward.z)
  if (facing < 1e-5) return false
  return (dx * forward.x + dz * forward.z) / (distance * facing) < BEHIND_COS
}

/** A placement saved for the next session: the device's anchor handle and the hold. */
export interface SavedPlacement {
  readonly handle: string
  readonly hold: GlobeHold
}

/** The hold a handle saved before holds were saved gets: resting on its surface. */
const DEFAULT_HOLD: GlobeHold = { kind: 'rest', gap: SURFACE_GAP_M }

/** The stored form (`vrPersistence.ts` keeps it as it is given). */
export function encodeSavedPlacement(saved: SavedPlacement): string {
  return JSON.stringify({ v: 1, handle: saved.handle, hold: saved.hold })
}

/**
 * Read a stored placement. A bare handle (saved before 2026-11-03) rests
 * on its surface; anything unreadable is null, so the caller clears it.
 */
export function decodeSavedPlacement(raw: string | null): SavedPlacement | null {
  if (!raw) return null
  if (!raw.startsWith('{')) return { handle: raw, hold: DEFAULT_HOLD }
  try {
    const parsed = JSON.parse(raw) as { handle?: unknown; hold?: { kind?: unknown; gap?: unknown; offset?: unknown } }
    if (typeof parsed.handle !== 'string' || parsed.handle === '') return null
    const h = parsed.hold
    const finite = (n: unknown, lo: number, hi: number): n is number => typeof n === 'number' && Number.isFinite(n) && n >= lo && n <= hi
    if (h?.kind === 'rest' && finite(h.gap, 0, 1)) return { handle: parsed.handle, hold: { kind: 'rest', gap: h.gap } }
    if (h?.kind === 'height' && finite(h.offset, -1, 3)) return { handle: parsed.handle, hold: { kind: 'height', offset: h.offset } }
    return { handle: parsed.handle, hold: DEFAULT_HOLD }
  } catch {
    return null
  }
}
